import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import chatService from '../services/chatService';
import { useAuth } from '../contexts/AuthContext';
import './Chat.css';

const Chat = () => {
  const { isAuthenticated, currentUser } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [newChatTitle, setNewChatTitle] = useState('');
  const [newChatModel, setNewChatModel] = useState('gpt-4o');
  const [newChatSystemPrompt, setNewChatSystemPrompt] = useState('You are a helpful assistant.');
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});
  const [isPolling, setIsPolling] = useState(false);
  const messagesEndRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const pollingCountRef = useRef(0);

  // Fetch conversations on component mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchConversations();
    }
    
    // Clean up polling interval on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [isAuthenticated]);

  // Scroll to bottom of messages when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Function to start polling for new messages
  const startPollingMessages = (conversationId) => {
    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    setIsPolling(true);
    pollingCountRef.current = 0;
    
    // Poll every 2 seconds, up to 10 times (20 seconds total)
    pollingIntervalRef.current = setInterval(() => {
      pollingCountRef.current += 1;
      
      // Fetch messages
      fetchMessagesForConversation(conversationId);
      
      // Stop polling after 10 attempts or if we're no longer sending
      if (pollingCountRef.current >= 10 || !isSending) {
        clearInterval(pollingIntervalRef.current);
        setIsPolling(false);
      }
    }, 2000);
  };
  
  // Function to fetch messages for a conversation
  const fetchMessagesForConversation = async (conversationId) => {
    try {
      const response = await chatService.getMessages(conversationId);
      console.log(`Polled messages for conversation ${conversationId}:`, response);
      
      // Handle different response formats
      let messagesData = [];
      
      if (Array.isArray(response)) {
        messagesData = response;
      } else if (Array.isArray(response.data)) {
        messagesData = response.data;
      } else if (response.data && Array.isArray(response.data.messages)) {
        messagesData = response.data.messages;
      } else if (response.messages && Array.isArray(response.messages)) {
        messagesData = response.messages;
      }
      
      if (messagesData.length > 0) {
        // Only update if we have more messages than before
        setMessages(prevMessages => {
          if (messagesData.length > prevMessages.length) {
            return messagesData;
          }
          return prevMessages;
        });
      }
    } catch (error) {
      console.error(`Error polling messages for conversation ${conversationId}:`, error);
    }
  };

  const fetchConversations = async () => {
    try {
      setIsLoading(true);
      const response = await chatService.getConversations();
      console.log('Fetched conversations response:', response);
      
      // Ensure we have valid data
      let conversationsData = [];
      
      // Handle different response formats
      if (Array.isArray(response)) {
        // Direct array response
        conversationsData = response;
      } else if (Array.isArray(response.data)) {
        // Array in data property
        conversationsData = response.data;
      } else if (response.data && Array.isArray(response.data.conversations)) {
        // Nested array in data.conversations
        conversationsData = response.data.conversations;
      } else if (response.conversations && Array.isArray(response.conversations)) {
        // Array in conversations property
        conversationsData = response.conversations;
      } else if (typeof response.data === 'object' && response.data !== null) {
        // If response.data is an object but not an array, it might be a single conversation
        conversationsData = [response.data];
      } else if (typeof response === 'object' && response !== null && !Array.isArray(response)) {
        // If response itself is an object (not array), it might be a single conversation
        conversationsData = [response];
      }
      
      console.log('Processed conversations data:', conversationsData);
      setConversations(conversationsData);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setIsLoading(false);
    }
  };

  const selectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    try {
      setIsLoading(true);
      const response = await chatService.getMessages(conversation.id);
      console.log(`Messages for conversation ${conversation.id}:`, response);
      
      // Handle different response formats
      let messagesData = [];
      
      if (Array.isArray(response)) {
        // Direct array response
        messagesData = response;
      } else if (Array.isArray(response.data)) {
        // Array in data property
        messagesData = response.data;
      } else if (response.data && Array.isArray(response.data.messages)) {
        // Nested array in data.messages
        messagesData = response.data.messages;
      } else if (response.messages && Array.isArray(response.messages)) {
        // Array in messages property
        messagesData = response.messages;
      }
      
      console.log('Processed messages data:', messagesData);
      setMessages(messagesData);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !selectedConversation) return;
    
    try {
      setIsSending(true);
      
      // First, create a user message to show immediately in the UI
      const userMessageContent = newMessage.trim();
      const tempUserMessage = {
        id: `temp-${Date.now()}`,
        conversation_id: selectedConversation.id,
        role: 'user',
        content: userMessageContent,
        created_at: new Date().toISOString()
      };
      
      // Add the user message to the UI immediately
      setMessages([...messages, tempUserMessage]);
      
      // Clear the input field
      setNewMessage('');
      
      // Send the message to the API
      const response = await chatService.sendMessage(selectedConversation.id, userMessageContent);
      console.log('Send message response:', response);
      
      // Start polling for new messages
      startPollingMessages(selectedConversation.id);
      
      // After sending the message, fetch all messages for the conversation to ensure we have the latest
      try {
        const messagesResponse = await chatService.getMessages(selectedConversation.id);
        console.log('Refreshed messages after sending:', messagesResponse);
        
        // Handle different response formats for messages
        let messagesData = [];
        
        if (Array.isArray(messagesResponse)) {
          // Direct array response
          messagesData = messagesResponse;
        } else if (Array.isArray(messagesResponse.data)) {
          // Array in data property
          messagesData = messagesResponse.data;
        } else if (messagesResponse.data && Array.isArray(messagesResponse.data.messages)) {
          // Nested array in data.messages
          messagesData = messagesResponse.data.messages;
        } else if (messagesResponse.messages && Array.isArray(messagesResponse.messages)) {
          // Array in messages property
          messagesData = messagesResponse.messages;
        }
        
        if (messagesData.length > 0) {
          // Update the messages state with the fresh data
          setMessages(messagesData);
        } else {
          // If we didn't get any messages back, try to extract from the original response
          handleMessageResponse(response, tempUserMessage);
        }
      } catch (error) {
        console.error('Error refreshing messages after sending:', error);
        // If refreshing messages fails, try to extract from the original response
        handleMessageResponse(response, tempUserMessage);
      }
      
      setIsSending(false);
    } catch (error) {
      console.error('Error sending message:', error);
      setIsSending(false);
      
      // Show error in UI
      setMessages(prevMessages => [
        ...prevMessages, 
        {
          id: `error-${Date.now()}`,
          role: 'system',
          content: 'Error sending message. Please try again.',
          created_at: new Date().toISOString()
        }
      ]);
    }
  };
  
  // Helper function to handle message response
  const handleMessageResponse = (response, tempUserMessage) => {
    if (response.data) {
      if (response.data.userMessage && response.data.assistantMessage) {
        // Replace the temporary user message with the real one and add the assistant message
        setMessages(prevMessages => {
          // Filter out the temporary message
          const filteredMessages = prevMessages.filter(msg => msg.id !== tempUserMessage.id);
          // Add the real messages
          return [...filteredMessages, response.data.userMessage, response.data.assistantMessage];
        });
      } else if (Array.isArray(response.data) && response.data.length > 0) {
        // If the response is an array of messages, use those
        setMessages(prevMessages => {
          // Filter out the temporary message
          const filteredMessages = prevMessages.filter(msg => msg.id !== tempUserMessage.id);
          // Add the new messages
          return [...filteredMessages, ...response.data];
        });
      } else if (response.data.message || response.data.content) {
        // If it's a single message response
        const assistantMessage = {
          id: `assistant-${Date.now()}`,
          conversation_id: selectedConversation.id,
          role: 'assistant',
          content: response.data.message || response.data.content,
          created_at: new Date().toISOString()
        };
        
        setMessages(prevMessages => [...prevMessages, assistantMessage]);
      }
    } else if (response.message || response.content) {
      // Direct response with message content
      const assistantMessage = {
        id: `assistant-${Date.now()}`,
        conversation_id: selectedConversation.id,
        role: 'assistant',
        content: response.message || response.content,
        created_at: new Date().toISOString()
      };
      
      setMessages(prevMessages => [...prevMessages, assistantMessage]);
    }
  };

  const createNewConversation = async () => {
    try {
      setIsLoading(true);
      const response = await chatService.createConversation({
        title: newChatTitle || 'New Conversation',
        model: newChatModel,
        system_prompt: newChatSystemPrompt
      });
      
      console.log('Create conversation response:', response);
      
      // Handle the response
      let newConversation = null;
      
      if (response && response.data) {
        newConversation = response.data;
      } else if (response && !response.data && typeof response === 'object') {
        // The response itself might be the conversation object
        newConversation = response;
      }
      
      if (newConversation && newConversation.id) {
        // Refresh the conversations list to ensure we have the latest data
        await fetchConversations();
        
        // Set the selected conversation
        setSelectedConversation(newConversation);
        setMessages([]);
        
        // Close the modal
        setShowNewChatModal(false);
        setNewChatTitle('');
      } else {
        console.error('Failed to create conversation: Invalid response format', response);
        alert('Failed to create conversation. Please try again.');
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error creating conversation:', error);
      setIsLoading(false);
      alert('Failed to create conversation: ' + (error.message || 'Unknown error'));
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchConversations();
      return;
    }
    
    try {
      setIsLoading(true);
      const response = await chatService.searchConversations(searchQuery);
      // Ensure we have valid data
      const searchResults = Array.isArray(response.data) ? response.data : [];
      setConversations(searchResults);
      setIsLoading(false);
    } catch (error) {
      console.error('Error searching conversations:', error);
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Unknown date';
    }
  };

  const getMessagePreview = (conversation) => {
    // Safely check for messages
    if (!conversation) return 'No messages yet';
    
    // Check if conversation has messages array
    if (conversation.messages && Array.isArray(conversation.messages) && conversation.messages.length > 0) {
      const lastMessage = conversation.messages[conversation.messages.length - 1];
      if (lastMessage && lastMessage.content) {
        return lastMessage.content.substring(0, 50) + (lastMessage.content.length > 50 ? '...' : '');
      }
    }
    
    // Check if conversation has a lastMessage property
    if (conversation.lastMessage && conversation.lastMessage.content) {
      return conversation.lastMessage.content.substring(0, 50) + 
        (conversation.lastMessage.content.length > 50 ? '...' : '');
    }
    
    return 'No messages yet';
  };

  // Debug function to test API endpoints
  const runDebugTests = async () => {
    setDebugInfo({});
    setShowDebugInfo(true);
    
    try {
      // Test getConversations
      const debugResults = {};
      
      debugResults.getConversations = { status: 'running' };
      setDebugInfo(debugResults);
      
      try {
        const conversationsResponse = await chatService.getConversations();
        debugResults.getConversations = { 
          status: 'success', 
          data: conversationsResponse 
        };
      } catch (error) {
        debugResults.getConversations = { 
          status: 'error', 
          error: error.toString(),
          message: error.message
        };
      }
      
      setDebugInfo({...debugResults});
      
      // Test createConversation
      debugResults.createConversation = { status: 'running' };
      setDebugInfo({...debugResults});
      
      try {
        const createResponse = await chatService.createConversation({
          title: 'Debug Test Conversation',
          model: 'gpt-4o',
          system_prompt: 'Debug test prompt'
        });
        
        debugResults.createConversation = { 
          status: 'success', 
          data: createResponse 
        };
        
        // If we successfully created a conversation, test sending a message
        if (createResponse && createResponse.data && createResponse.data.id) {
          const conversationId = createResponse.data.id;
          
          // Test getMessages
          debugResults.getMessages = { status: 'running' };
          setDebugInfo({...debugResults});
          
          try {
            const messagesResponse = await chatService.getMessages(conversationId);
            debugResults.getMessages = { 
              status: 'success', 
              data: messagesResponse 
            };
          } catch (error) {
            debugResults.getMessages = { 
              status: 'error', 
              error: error.toString(),
              message: error.message
            };
          }
          
          // Test sendMessage
          debugResults.sendMessage = { status: 'running' };
          setDebugInfo({...debugResults});
          
          try {
            const sendResponse = await chatService.sendMessage(
              conversationId, 
              'This is a debug test message'
            );
            debugResults.sendMessage = { 
              status: 'success', 
              data: sendResponse 
            };
          } catch (error) {
            debugResults.sendMessage = { 
              status: 'error', 
              error: error.toString(),
              message: error.message
            };
          }
        }
      } catch (error) {
        debugResults.createConversation = { 
          status: 'error', 
          error: error.toString(),
          message: error.message
        };
      }
      
      setDebugInfo({...debugResults});
    } catch (error) {
      console.error('Debug test error:', error);
      setDebugInfo({
        error: error.toString(),
        message: error.message
      });
    }
  };

  return (
    <div className="chat-container">
      {/* Sidebar with conversations */}
      <div className="chat-sidebar">
        <div className="sidebar-header">
          <h2>Conversations</h2>
          <div className="sidebar-actions">
            <button 
              className="refresh-button"
              onClick={fetchConversations}
              disabled={isLoading}
              title="Refresh conversations"
            >
              <span>🔄</span>
            </button>
            <button 
              className="new-chat-button"
              onClick={() => setShowNewChatModal(true)}
              title="New conversation"
            >
              <span>+</span>
            </button>
          </div>
        </div>
        
        <div className="search-container">
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button onClick={handleSearch}>🔍</button>
        </div>
        
        <div className="conversations-list">
          {isLoading && conversations.length === 0 ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Loading conversations...</p>
            </div>
          ) : conversations.length === 0 ? (
            <div className="no-conversations">
              <p>No conversations found</p>
              <button onClick={() => setShowNewChatModal(true)}>Start a new conversation</button>
            </div>
          ) : (
            conversations.map(conversation => (
              <div 
                key={conversation.id || Math.random().toString()} 
                className={`conversation-item ${selectedConversation?.id === conversation.id ? 'selected' : ''}`}
                onClick={() => conversation.id && selectConversation(conversation)}
              >
                <div className="conversation-icon">
                  {conversation.model === 'gpt-4o' ? '🧠' : '💬'}
                </div>
                <div className="conversation-details">
                  <h3>{conversation.title || 'Untitled Conversation'}</h3>
                  <p className="conversation-preview">{getMessagePreview(conversation)}</p>
                  <span className="conversation-date">
                    {conversation.updated_at ? formatDate(conversation.updated_at) : 'Recent'}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* Main chat area */}
      <div className="chat-main">
        {selectedConversation ? (
          <>
            <div className="chat-header">
              <h2>{selectedConversation.title || 'Untitled Conversation'}</h2>
              <div className="chat-actions">
                <button className="action-button">
                  <span>⋮</span>
                </button>
              </div>
            </div>
            
            <div className="messages-container">
              {isLoading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Loading messages...</p>
                </div>
              ) : messages.length === 0 ? (
                <div className="no-messages">
                  <p>No messages yet</p>
                  <p>Start the conversation by sending a message below</p>
                </div>
              ) : (
                <>
                  {messages.map(message => (
                    <div 
                      key={message.id || Math.random().toString()} 
                      className={`message ${message.role === 'user' ? 'user-message' : 'assistant-message'}`}
                    >
                      <div className="message-content">
                        {message.content || ''}
                      </div>
                      <div className="message-timestamp">
                        {message.created_at ? formatDate(message.created_at) : 'Just now'}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>
            
            <form className="message-input-container" onSubmit={handleSendMessage}>
              <input
                type="text"
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                disabled={isSending}
              />
              <button 
                type="submit" 
                disabled={isSending || !newMessage.trim()}
                className={isSending ? 'sending' : ''}
              >
                {isSending ? <div className="spinner small"></div> : '➤'}
              </button>
            </form>
            {isPolling && (
              <div className="polling-indicator">
                <div className="spinner small"></div>
                <span>Waiting for response...</span>
              </div>
            )}
          </>
        ) : (
          <div className="no-conversation-selected">
            <div className="welcome-message">
              <h2>Welcome to Chat</h2>
              <p>Select a conversation from the sidebar or start a new one</p>
              <button 
                className="new-chat-button large"
                onClick={() => setShowNewChatModal(true)}
              >
                Start a new conversation
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* New Chat Modal */}
      {showNewChatModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>New Conversation</h2>
            
            <div className="form-group">
              <label htmlFor="chat-title">Title</label>
              <input
                id="chat-title"
                type="text"
                placeholder="Enter a title for your conversation"
                value={newChatTitle}
                onChange={(e) => setNewChatTitle(e.target.value)}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="chat-model">Model</label>
              <select
                id="chat-model"
                value={newChatModel}
                onChange={(e) => setNewChatModel(e.target.value)}
              >
                <option value="gpt-4o">GPT-4o</option>
                <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="system-prompt">System Prompt</label>
              <textarea
                id="system-prompt"
                placeholder="Enter a system prompt to set the context"
                value={newChatSystemPrompt}
                onChange={(e) => setNewChatSystemPrompt(e.target.value)}
                rows={3}
              />
            </div>
            
            <div className="modal-actions">
              <button 
                className="cancel-button"
                onClick={() => setShowNewChatModal(false)}
              >
                Cancel
              </button>
              <button 
                className="create-button"
                onClick={createNewConversation}
                disabled={isLoading}
              >
                {isLoading ? <div className="spinner small"></div> : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Debug Modal */}
      {showDebugInfo && (
        <div className="modal-overlay">
          <div className="modal-content debug-modal">
            <h2>API Debug Information</h2>
            
            <div className="debug-actions">
              <button 
                className="debug-test-button"
                onClick={runDebugTests}
              >
                Run API Tests
              </button>
              <button 
                className="cancel-button"
                onClick={() => setShowDebugInfo(false)}
              >
                Close
              </button>
            </div>
            
            <div className="debug-results">
              <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
            </div>
          </div>
        </div>
      )}
      
      {/* Add debug button at the bottom of the page */}
      <button 
        className="debug-button"
        onClick={() => setShowDebugInfo(true)}
        title="Debug API"
      >
        🐞
      </button>
    </div>
  );
};

export default Chat; 